import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  Typography
} from "@mui/material";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  WatchInternal,
} from "react-hook-form";
import { falModels } from "../../ImageGenerator/constants";

interface Props {
  errors: FieldErrors<any>;
  register: UseFormRegister<any>;
  systemType: string;
  setSystemType: React.Dispatch<React.SetStateAction<string>>;
  watch: WatchInternal<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  systems: any[];
}

const FalConfigForm = ({
  errors,
  register,
  setSystemType,
  setValue,
  systemType,
  control,
  watch,
  systems,
}: Props) => {
  const falImageSizeTypes = [
    'square_hd',
    'square',
    'portrait_4_3',
    'portrait_16_9',
    'landscape_4_3',
    'landscape_16_9',
  ];

  return (
    <Box>
      <Box
        mt={'16px'}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '32px',
        }}
      >
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="ai-system-select-label">Ai System</InputLabel>
          <Select
            name="AiSystem"
            labelId="ai-system-select-label"
            id="ai-system-select"
            label="Ai System"
            value={systemType}
            onChange={e => {
              setSystemType(e.target.value);
            }}
            sx={{
              width: '100%',
            }}
          >
            {systems.map((opt, index) => (
              <MenuItem
                key={index}
                value={opt.system}
                disabled={
                  opt.models && opt.models.length > 0
                    ? opt.models.every((model: any) => model.disabledByCompany)
                    : false
                }
              >
                {opt.system === 'fal'
                  ? 'Flux'
                  : opt.system.charAt(0).toUpperCase() + opt.system.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="fal-model-label">Flux Model</InputLabel>
          <Select
            value={watch().falModel}
            labelId="fal-model-label"
            id="fal-model-select"
            label="Flux Model"
            {...register('falModel')}
            sx={{
              width: '100%',
            }}
          >
            {falModels.map((model, index) => (
              <MenuItem key={index} value={model}>
                {model}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        mt={'16px'}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '32px',
        }}
      >
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="image-size-label">Image Size</InputLabel>
          <Select
            value={watch().imageSize}
            labelId="image-size-label"
            id="image-size-select"
            label="Image Size"
            {...register('imageSize')}
            sx={{
              width: '100%',
            }}
          >
            {falImageSizeTypes.map((size, index) => (
              <MenuItem key={index} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <FormControlLabel
            control={
              <Switch
                {...register('sync_mode')}
                checked={watch().sync_mode}
                onChange={(e) => setValue('sync_mode', e.target.checked)}
              />
            }
            label="Sync Mode"
            sx={{ justifyContent: 'center' }}
          />
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: '16px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography fontSize={'14px'} fontWeight={500}>
            Inference Steps
          </Typography>
          <Slider
            {...register('inference_steps')}
            step={1}
            min={1}
            max={50}
            value={Number(watch()?.inference_steps)}
            onChange={(event, value) => setValue('inference_steps', value)}
          />
          <Typography
            fontSize={'14px'}
            fontWeight={400}
            color={'rgba(0, 0, 0, 0.60)'}
          >
            {watch()?.inference_steps}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography fontSize={'14px'} fontWeight={500}>
            Seed
          </Typography>
          <Slider
            {...register('fal_seed')}
            step={1}
            min={0}
            max={1000000}
            value={Number(watch()?.fal_seed)}
            onChange={(event, value) => setValue('fal_seed', value)}
          />
          <Typography
            fontSize={'14px'}
            fontWeight={400}
            color={'rgba(0, 0, 0, 0.60)'}
          >
            {watch()?.fal_seed}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography fontSize={'14px'} fontWeight={500}>
            Guidance Scale
          </Typography>
          <Slider
            {...register('guidance_scalefloat')}
            step={0.1}
            min={0}
            max={20}
            value={Number(watch()?.guidance_scalefloat)}
            onChange={(event, value) => setValue('guidance_scalefloat', value)}
          />
          <Typography
            fontSize={'14px'}
            fontWeight={400}
            color={'rgba(0, 0, 0, 0.60)'}
          >
            {watch()?.guidance_scalefloat}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography fontSize={'14px'} fontWeight={500}>
            Safety Tolerance
          </Typography>
          <Slider
            {...register('safety_tolerance')}
            step={1}
            min={1}
            max={6}
            value={Number(watch()?.safety_tolerance)}
            onChange={(event, value) => setValue('safety_tolerance', value)}
          />
          <Typography
            fontSize={'14px'}
            fontWeight={400}
            color={'rgba(0, 0, 0, 0.60)'}
          >
            {watch()?.safety_tolerance}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FalConfigForm;